import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'notistack';
import api, { VP_SUPERTREND } from '../../infrastructure/config/api';

const DEFAULT_SYMBOL = 'NAORISUSDT';
const TIMEFRAME = '15m';
const NOTIONAL_OPTIONS = [100, 200, 300, 400, 500];

function formatTime(value) {
  if (!value) return '-';
  return new Date(Number(value)).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
}

function formatNumber(value, digits = 2) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(digits) : '-';
}

const StatCard = ({ label, value, color }) => (
  <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 140 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h6" sx={{ color }}>{value}</Typography>
  </Paper>
);

const VpSupertrendPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [signals, setSignals] = useState(null);
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState({ enabled: false, dryRun: true, notionalUsdt: 100, leverage: 10 });
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [configs, setConfigs] = useState([]);
  const [symbolOptions, setSymbolOptions] = useState([]);

  // Danh sách symbol đã cấu hình: dùng để chuyển nhanh và để chọn symbol mở đầu.
  const loadConfigs = useCallback(async () => {
    try {
      const { data } = await api.get(VP_SUPERTREND.CONFIGS);
      const rows = data?.data || [];
      setConfigs(rows);
      return rows;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [rows] = await Promise.all([
        loadConfigs(),
        api.get(VP_SUPERTREND.BINANCE_SYMBOLS)
          .then(({ data }) => { if (!cancelled) setSymbolOptions(data?.data || []); })
          .catch(() => {}),
      ]);
      if (!cancelled && rows.length && !rows.some((row) => row.symbol === DEFAULT_SYMBOL)) {
        setSymbol(rows[0].symbol);
      }
    })();
    return () => { cancelled = true; };
  }, [loadConfigs]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, signalsRes, tradesRes] = await Promise.all([
        api.get(VP_SUPERTREND.STATUS, { params: { symbol, timeframe: TIMEFRAME } }),
        api.get(VP_SUPERTREND.SIGNALS, { params: { symbol, timeframe: TIMEFRAME, limit: 15 } }),
        api.get(VP_SUPERTREND.TRADES, { params: { symbol, timeframe: TIMEFRAME, limit: 50 } }),
      ]);

      const data = statusRes.data?.data;
      setStatus(data);
      setSignals(signalsRes.data?.data || null);
      setTrades(tradesRes.data?.data || []);

      // Symbol chưa có cấu hình -> hiện giá trị mặc định thay vì giữ số của symbol trước.
      setForm(data?.config
        ? {
          enabled: Boolean(data.config.enabled),
          dryRun: Boolean(data.config.dry_run),
          notionalUsdt: Number(data.config.notional_usdt) || 100,
          leverage: Number(data.config.leverage) || 10,
        }
        : { enabled: false, dryRun: true, notionalUsdt: 100, leverage: 10 });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Không tải được dữ liệu', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, symbol]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const next = { ...form, ...patch };
      await api.put(VP_SUPERTREND.CONFIG, { symbol, timeframe: TIMEFRAME, ...next });
      setForm(next);
      enqueueSnackbar(`Đã lưu cấu hình ${symbol}`, { variant: 'success' });
      loadConfigs();
      loadAll();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Lưu cấu hình thất bại', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const removeConfig = async (target) => {
    if (!window.confirm(`Xoá cấu hình bot cho ${target}?`)) return;
    setSaving(true);
    try {
      await api.delete(VP_SUPERTREND.CONFIG, { params: { symbol: target, timeframe: TIMEFRAME } });
      enqueueSnackbar(`Đã xoá cấu hình ${target}`, { variant: 'success' });
      const rows = await loadConfigs();
      if (target === symbol) setSymbol(rows[0]?.symbol || DEFAULT_SYMBOL);
      else loadAll();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Xoá cấu hình thất bại', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setSaving(true);
    try {
      const res = await api.post(VP_SUPERTREND.RUN_NOW, { symbol, timeframe: TIMEFRAME });
      const run = res.data?.data?.runs?.[0];
      enqueueSnackbar(
        run?.signal ? `Tín hiệu ${run.signal.side} tại nến ${formatTime(run.signal.signalBarTime)}` : 'Nến vừa đóng không có tín hiệu',
        { variant: run?.signal ? 'success' : 'info' }
      );
      loadAll();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.error || 'Chạy thủ công thất bại', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
    );
  }

  const stats = form.dryRun ? status?.stats?.dryRun : status?.stats?.live;
  const totalPnl = Number(stats?.total_pnl || 0);
  const openTrade = status?.openTrade;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">VP Supertrend Bot</Typography>
          <Typography variant="body2" color="text.secondary">
            {symbol} · {TIMEFRAME} · vào MARKET tại nến N+1, thoát khi có tín hiệu ngược
          </Typography>
        </Box>
        <Button startIcon={<AutorenewIcon />} onClick={loadAll} disabled={saving}>Làm mới</Button>
      </Stack>

      {form.dryRun && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Đang ở chế độ DRY-RUN: bot ghi nhật ký tín hiệu nhưng không gửi lệnh thật lên Binance.
        </Alert>
      )}
      {!status?.scheduler?.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Scheduler đang tắt vì server có biến <code>VP_SUPERTREND_ENABLE_SCHEDULER=false</code>.
          Xoá biến đó rồi khởi động lại app để chạy lại (mặc định là bật).
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Symbol</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
          <Autocomplete
            options={symbolOptions}
            value={symbol}
            disabled={saving}
            sx={{ minWidth: 260 }}
            onChange={(event, value) => { if (value) setSymbol(value); }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Chọn cặp giao dịch"
                helperText={symbolOptions.length ? `${symbolOptions.length} cặp USDT perp` : 'Đang tải danh sách...'}
              />
            )}
          />
          <Typography variant="body2" color="text.secondary">
            {status?.config
              ? 'Symbol này đã có cấu hình.'
              : 'Symbol này chưa có cấu hình — bấm lưu bất kỳ ô nào bên dưới để tạo.'}
          </Typography>
        </Stack>

        {configs.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary">Đã cấu hình:</Typography>
            {configs.map((item) => (
              <Chip
                key={item.id}
                label={`${item.symbol}${item.enabled ? '' : ' (tắt)'}`}
                size="small"
                color={item.symbol === symbol ? 'primary' : 'default'}
                variant={item.symbol === symbol ? 'filled' : 'outlined'}
                onClick={() => setSymbol(item.symbol)}
              />
            ))}
            <Tooltip title={`Xoá cấu hình ${symbol}`}>
              <span>
                <IconButton
                  size="small"
                  disabled={saving || !status?.config}
                  onClick={() => removeConfig(symbol)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>Cấu hình {symbol}</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <FormControlLabel
            control={<Switch checked={form.enabled} disabled={saving} onChange={(e) => saveConfig({ enabled: e.target.checked })} />}
            label={form.enabled ? 'Bot đang BẬT' : 'Bot đang TẮT'}
          />
          <FormControlLabel
            control={<Switch checked={form.dryRun} disabled={saving} onChange={(e) => saveConfig({ dryRun: e.target.checked })} />}
            label="Dry-run (không đặt lệnh thật)"
          />
          <TextField
            select size="small" label="Volume (USDT)" sx={{ minWidth: 160 }}
            value={form.notionalUsdt} disabled={saving}
            onChange={(e) => saveConfig({ notionalUsdt: Number(e.target.value) })}
          >
            {NOTIONAL_OPTIONS.map((value) => (
              <MenuItem key={value} value={value}>{value} USDT (ký quỹ {value / form.leverage} USDT)</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="Đòn bẩy" type="number" sx={{ width: 120 }}
            value={form.leverage} disabled={saving}
            onChange={(e) => setForm({ ...form, leverage: Number(e.target.value) })}
            onBlur={() => saveConfig({ leverage: form.leverage })}
          />
          <Button variant="outlined" onClick={runNow} disabled={saving || !form.enabled}>Chạy 1 lượt ngay</Button>
        </Stack>
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <StatCard label="Tổng lời/lỗ" value={`${totalPnl >= 0 ? '+' : ''}${formatNumber(totalPnl)} USDT`} color={totalPnl >= 0 ? 'success.main' : 'error.main'} />
        <StatCard label="Số lệnh đã đóng" value={stats?.total_trades ?? 0} />
        <StatCard label="Thắng / Thua" value={`${stats?.wins ?? 0} / ${stats?.losses ?? 0}`} />
        <StatCard label="Lệnh lãi nhất" value={`${formatNumber(stats?.best_trade)} USDT`} color="success.main" />
        <StatCard label="Lệnh lỗ nhất" value={`${formatNumber(stats?.worst_trade)} USDT`} color="error.main" />
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1">Vị thế đang mở</Typography>
        <Divider sx={{ my: 1 }} />
        {openTrade ? (
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Chip label={openTrade.side} color={openTrade.side === 'LONG' ? 'success' : 'error'} />
            <Typography variant="body2">Giá vào: {openTrade.entry_price}</Typography>
            <Typography variant="body2">Khối lượng: {openTrade.quantity}</Typography>
            <Typography variant="body2">Nến tín hiệu: {formatTime(openTrade.signal_bar_time)}</Typography>
            <Typography variant="body2">Nến vào lệnh: {formatTime(openTrade.entry_bar_time)}</Typography>
            {openTrade.dry_run && <Chip size="small" label="DRY-RUN" />}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">Chưa có vị thế nào đang mở.</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">Tín hiệu gần đây (đối chiếu với label trên TradingView)</Typography>
          {signals && (
            <Chip
              size="small"
              label={`Trend: ${signals.currentTrend}`}
              color={signals.currentTrend === 'BULLISH' ? 'success' : 'error'}
            />
          )}
        </Stack>
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tín hiệu</TableCell>
                <TableCell>Nến N (in label)</TableCell>
                <TableCell align="right">Giá đóng N</TableCell>
                <TableCell>Nến N+1 (vào lệnh)</TableCell>
                <TableCell align="right">Giá mở N+1</TableCell>
                <TableCell align="right">Strength</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(signals?.signals || []).slice().reverse().map((signal) => (
                <TableRow key={signal.signalBarTime}>
                  <TableCell>
                    <Chip size="small" label={signal.side} color={signal.side === 'BUY' ? 'success' : 'error'} />
                  </TableCell>
                  <TableCell>{formatTime(signal.signalBarTime)}</TableCell>
                  <TableCell align="right">{signal.signalClose}</TableCell>
                  <TableCell>{formatTime(signal.entryBarTime)}</TableCell>
                  <TableCell align="right">{signal.entryBarOpen ?? '-'}</TableCell>
                  <TableCell align="right">{signal.strength}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Lịch sử lệnh của bot</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Chiều</TableCell>
                <TableCell>Trạng thái</TableCell>
                <TableCell>Nến vào lệnh</TableCell>
                <TableCell align="right">Giá vào</TableCell>
                <TableCell align="right">Giá ra</TableCell>
                <TableCell align="right">Vol (USDT)</TableCell>
                <TableCell align="right">Lời/Lỗ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trades.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">Chưa có lệnh nào.</TableCell></TableRow>
              )}
              {trades.map((trade) => {
                const pnl = Number(trade.realized_pnl);
                return (
                  <TableRow key={trade.id}>
                    <TableCell>
                      <Chip size="small" label={trade.side} color={trade.side === 'LONG' ? 'success' : 'error'} />
                      {trade.dry_run && <Chip size="small" label="DRY" sx={{ ml: 0.5 }} />}
                    </TableCell>
                    <TableCell>{trade.status}</TableCell>
                    <TableCell>{formatTime(trade.entry_bar_time)}</TableCell>
                    <TableCell align="right">{trade.entry_price ?? '-'}</TableCell>
                    <TableCell align="right">{trade.exit_price ?? '-'}</TableCell>
                    <TableCell align="right">{formatNumber(trade.notional_usdt, 0)}</TableCell>
                    <TableCell align="right" sx={{ color: pnl >= 0 ? 'success.main' : 'error.main' }}>
                      {Number.isFinite(pnl) ? `${pnl >= 0 ? '+' : ''}${formatNumber(pnl)}` : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default VpSupertrendPage;
